import { traducirError, traducirProblema, useTraduccion, type Idioma } from '@tallerya/i18n';
import { AvisoError, Boton, useAvisos, Ventana } from '@tallerya/ui';
import { useEffect, useState } from 'react';
import { useCambiosCliente, version, type Cliente, type ProblemaCliente } from './datos';

// «Clientes - Eliminar» y «No se puede eliminar» del Figma. Confirma antes de la
// baja, que es lógica: el cliente deja de aparecer en listados y búsquedas.
//
// Si el cliente tiene historial —alguna orden o algún activo—, la API responde
// 409 con cuántos y si sigue activo (#177), y la ventana pasa a explicarlo: ya no
// ofrece eliminar. Si sigue activo, ofrece «Desactivar cliente», que es lo que
// corresponde a quien ya no es cliente. Cualquier otro rechazo se dice adentro y
// la ventana sigue abierta, como en sucursales.

interface Props {
  /** Lo que dice el texto, y la versión para desactivarlo. Sirve la fila del listado y la ficha. */
  cliente: Pick<Cliente, 'id' | 'name' | 'code' | 'version'> | null;
  onCerrar: () => void;
  /** Después de eliminarlo. La edición vuelve al listado; el listado no hace nada más. */
  onEliminado?: () => void;
  /**
   * Solo la edición: hay campos cambiados sin guardar. Desactivar recarga el
   * formulario, así que antes se pregunta qué hacer con ellos (#177).
   */
  cambiosSinGuardar?: boolean;
  /**
   * Solo la edición: guarda el formulario con el cliente inactivo, en un solo
   * guardado, y avisa. Devuelve si salió bien; si no, el formulario ya dice por qué.
   *
   * Que venga dice también que la pantalla se rearma al recargar el cliente —la
   * edición lleva la versión en su `key`—: tras un rechazo, la recarga espera a
   * que se cierre la ventana, o se la llevaría con su aviso sin que se lea.
   */
  guardarYDesactivar?: () => Promise<boolean>;
}

/** Por qué no se pudo: el historial que lo impide, u otro problema ya traducido. */
type Rechazo =
  | { tipo: 'historial'; ordenes: number; activos: number; activo: boolean }
  | { tipo: 'otro'; texto: string };

export function EliminarCliente({
  cliente,
  onCerrar,
  onEliminado,
  cambiosSinGuardar = false,
  guardarYDesactivar,
}: Props) {
  const { t, idioma } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosCliente();
  const [ocupado, setOcupado] = useState(false);
  const [rechazo, setRechazo] = useState<Rechazo | null>(null);
  /** Lo que falló al desactivar desde la ventana del rechazo. */
  const [fallo, setFallo] = useState<string | null>(null);
  /** «Desactivar cliente» con cambios sin guardar: ¿se guardan o se descartan? */
  const [preguntando, setPreguntando] = useState(false);
  /** En la edición, un rechazo deja la recarga para cuando se cierre la ventana. */
  const [recargarAlCerrar, setRecargarAlCerrar] = useState(false);

  // `Ventana` enfoca el primer botón, que en la pregunta es «Descartar y
  // desactivar»: un Enter perdería lo escrito. Este efecto corre después del de
  // `Ventana` —el del hijo va primero— y deja el foco en «Guardar y desactivar».
  useEffect(() => {
    if (preguntando) document.querySelector<HTMLElement>('dialog[open] [data-foco-inicial]')?.focus();
  }, [preguntando]);

  // El rechazo es de ESTE cliente: se borra al cerrar, o la próxima ventana
  // —con otro cliente— arrancaría diciendo que no se puede.
  function cerrar() {
    if (ocupado) return;
    if (recargarAlCerrar) void cambios.refrescar();
    setRechazo(null);
    setFallo(null);
    setPreguntando(false);
    setRecargarAlCerrar(false);
    onCerrar();
  }

  async function eliminar() {
    if (!cliente) return;
    setOcupado(true);
    setRechazo(null);
    let error: ProblemaCliente | undefined;
    try {
      error = await cambios.eliminar(String(cliente.id));
    } catch {
      setRechazo({ tipo: 'otro', texto: t('ui.formulario.aviso.sinRespuesta') });
      return;
    } finally {
      setOcupado(false);
    }

    if (error) {
      setRechazo(
        error.code === 'customer.tiene_historial'
          ? {
              tipo: 'historial',
              ordenes: Number(error.orderCount ?? 0),
              activos: Number(error.assetCount ?? 0),
              activo: error.isActive !== false,
            }
          : {
              tipo: 'otro',
              texto: error.code
                ? traducirError(error.code, t)
                : t('ui.formulario.aviso.sinRespuesta'),
            },
      );
      return;
    }
    avisar({ titulo: t('admin.clientes.aviso.eliminado', { nombre: cliente.name }) });
    setRechazo(null);
    onCerrar();
    onEliminado?.();
  }

  /** La salida que ofrece el rechazo. Como el «Desactivar» del menú: directo, sin otra confirmación. */
  async function desactivar() {
    if (!cliente) return;
    setOcupado(true);
    setFallo(null);
    try {
      const error = await cambios.cambiarEstado(String(cliente.id), version(cliente.version), false);
      if (!error) {
        avisar({ titulo: t('admin.clientes.aviso.desactivado', { nombre: cliente.name }) });
        setRechazo(null);
        setPreguntando(false);
        onCerrar();
        return;
      }

      const conflicto = error.code === 'conflict.cambio_simultaneo';
      const texto = error.code ? traducirProblema(error, t) : t('ui.formulario.aviso.sinRespuesta');
      if (guardarYDesactivar) {
        // La edición: recargar ahora la rearmaría y la ventana se iría sin que se
        // lea el aviso. Se recarga al cerrar, y hasta entonces no hay reintento.
        setRecargarAlCerrar(true);
        setFallo(conflicto ? t('admin.clientes.eliminar.desactivarConflictoEdicion') : texto);
        return;
      }
      // Con la versión de antes, reintentar chocaría siempre: se trae la nueva
      // —con los botones aún ocupados— y el aviso dice que ya se puede reintentar.
      await cambios.refrescar();
      setFallo(conflicto ? t('admin.clientes.eliminar.desactivarConflicto') : texto);
    } catch {
      setFallo(t('ui.formulario.aviso.sinRespuesta'));
    } finally {
      setOcupado(false);
    }
  }

  /**
   * «Guardar y desactivar»: el guardado del formulario, con el cliente inactivo.
   * El aviso lo da la edición, que sabe el nombre recién guardado. Si no se pudo,
   * la ventana se cierra para que se vea el formulario: ahí están el aviso y los
   * campos en rojo, y el cliente sigue como estaba.
   */
  async function guardarYLuegoDesactivar() {
    if (!guardarYDesactivar) return;
    setOcupado(true);
    // `enviar` no lanza: un fallo vuelve como `false`.
    await guardarYDesactivar().finally(() => setOcupado(false));
    setRechazo(null);
    setPreguntando(false);
    onCerrar();
  }

  /** Tras un rechazo en la edición, solo se puede cerrar: la recarga va al cerrar. */
  const soloEntendido = recargarAlCerrar && (
    <Boton onClick={cerrar}>{t('admin.clientes.eliminar.entendido')}</Boton>
  );

  // Las ventanas llevan `key`: sin ella React reusa el mismo `<dialog>`, que ya
  // está abierto, y `Ventana` no vuelve a llevar el foco adentro. El botón que lo
  // tenía —«Eliminar cliente»— desaparece, y el teclado queda en la página.
  if (preguntando && cliente) {
    return (
      <Ventana
        key="pregunta"
        abierta
        titulo={t('admin.clientes.eliminar.sinGuardar.titulo')}
        descripcion={t('admin.clientes.eliminar.sinGuardar.texto', { nombre: cliente.name })}
        ancho={560}
        acciones={
          soloEntendido || (
            <div className="cliente-desactivar__acciones">
              <Boton variante="texto" disabled={ocupado} onClick={cerrar}>
                {t('app.cancelar')}
              </Boton>
              <Boton variante="secundario" disabled={ocupado} onClick={() => void desactivar()}>
                {t('admin.clientes.eliminar.sinGuardar.descartar')}
              </Boton>
              <Boton
                data-foco-inicial
                disabled={ocupado}
                aria-busy={ocupado}
                onClick={() => void guardarYLuegoDesactivar()}
              >
                {t('admin.clientes.eliminar.sinGuardar.guardar')}
              </Boton>
            </div>
          )
        }
      >
        {fallo && <AvisoError aviso={{ titulo: t('admin.clientes.error.titulo'), texto: fallo }} />}
      </Ventana>
    );
  }

  if (rechazo?.tipo === 'historial' && cliente) {
    return (
      <Ventana
        key="rechazo"
        abierta
        titulo={t('admin.clientes.eliminar.bloqueado.titulo')}
        descripcion={t(
          rechazo.activo ? 'admin.clientes.eliminar.bloqueado.activo' : 'admin.clientes.eliminar.bloqueado.inactivo',
          { nombre: cliente.name, historial: historial(rechazo, t, idioma) },
        )}
        acciones={
          soloEntendido ||
          (rechazo.activo ? (
            <>
              <Boton variante="texto" disabled={ocupado} onClick={cerrar}>
                {t('admin.clientes.eliminar.entendido')}
              </Boton>
              <Boton
                disabled={ocupado}
                aria-busy={ocupado}
                onClick={() =>
                  cambiosSinGuardar && guardarYDesactivar ? setPreguntando(true) : void desactivar()
                }
              >
                {t('admin.clientes.eliminar.desactivar')}
              </Boton>
            </>
          ) : (
            <Boton onClick={cerrar}>{t('admin.clientes.eliminar.entendido')}</Boton>
          ))
        }
      >
        {fallo && <AvisoError aviso={{ titulo: t('admin.clientes.error.titulo'), texto: fallo }} />}
      </Ventana>
    );
  }

  return (
    <Ventana
      key="confirmar"
      abierta={cliente !== null}
      titulo={t('admin.clientes.eliminar.titulo')}
      descripcion={
        cliente &&
        t('admin.clientes.eliminar.texto', { nombre: cliente.name, codigo: cliente.code ?? '—' })
      }
      acciones={
        <>
          <Boton variante="texto" disabled={ocupado} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton
            variante="peligro"
            disabled={ocupado}
            aria-busy={ocupado}
            onClick={() => void eliminar()}
          >
            {t('admin.clientes.eliminar.boton')}
          </Boton>
        </>
      }
    >
      {rechazo?.tipo === 'otro' && (
        <AvisoError aviso={{ titulo: t('admin.clientes.error.titulo'), texto: rechazo.texto }} />
      )}
    </Ventana>
  );
}

/** «14 órdenes y 3 activos», con la conjunción del idioma. Solo nombra lo que tiene. */
function historial(
  { ordenes, activos }: { ordenes: number; activos: number },
  t: ReturnType<typeof useTraduccion>['t'],
  idioma: Idioma,
): string {
  const partes = [
    ordenes > 0 && t('admin.clientes.eliminar.bloqueado.ordenes', { n: ordenes }),
    activos > 0 && t('admin.clientes.eliminar.bloqueado.activos', { n: activos }),
  ].filter((parte): parte is string => typeof parte === 'string');

  return new Intl.ListFormat(idioma, { type: 'conjunction' }).format(partes);
}
